<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-lg-10 col-md-12">
        <div class="card">
          <div class="card-body p-4">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading details...</p>
            </div>

            <template v-else>
            <!-- Introductory Text -->
            <div class="mb-4">
              <p class="mb-3">{{ displayIntroText }}</p>
              
              <!-- Pending Swap Requests Button -->
              <MaterialButton 
                color="primary" 
                variant="fill"
                @click="viewPendingSwaps"
                class="mb-4"
              >
                All Pending Swap Requests
              </MaterialButton>
            </div>

            <!-- Heading -->
            <h4 class="fw-bold mb-4">Select a swap</h4>

            <!-- Current Assignment Details -->
            <div class="mb-4" v-if="displayCurrentAssignment">
              <div class="mb-2">
                <strong>Current Assignment Activity:</strong> {{ displayCurrentAssignment.activity }}
              </div>
              <div class="mb-2">
                <strong>Current Assignment Group:</strong> {{ displayCurrentAssignment.group }}
              </div>
              <div class="mb-2">
                <strong>Current Assignment Date:</strong> {{ displayCurrentAssignment.date }}
              </div>
              <div class="mb-0">
                <strong>Current Person Assigned:</strong> {{ displayCurrentAssignment.personAssigned }}
              </div>
            </div>

            <!-- Search Bar -->
            <div class="mb-4">
              <div class="input-group">
                <span class="input-group-text">search</span>
                <input 
                  type="text" 
                  class="form-control" 
                  placeholder="Type to search"
                  v-model="searchTerm"
                />
              </div>
            </div>

            <!-- Assignments Table -->
            <div class="table-responsive mb-4">
              <table class="table table-bordered">
                <thead>
                  <tr>
                    <th>ACTIVITY</th>
                    <th>WORKGROUP</th>
                    <th>DATE</th>
                    <th>PERSON ASSIGNED</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(assignment, index) in filteredAssignments" :key="index">
                    <td>{{ assignment.activity }}</td>
                    <td>{{ assignment.workgroup }}</td>
                    <td>{{ assignment.date }}</td>
                    <td>{{ assignment.personAssigned }}</td>
                    <td class="text-center">
                      <template v-if="assignment.available">
                        <div v-if="submittedSwaps.has(getAssignmentKey(assignment))" class="d-flex align-items-center justify-content-center gap-1">
                          <i class="material-icons text-success" style="font-size: 18px;">check_circle</i>
                          <span class="text-success text-sm fw-semibold">Request Submitted</span>
                        </div>
                        <MaterialButton 
                          v-else
                          color="success" 
                          variant="outline"
                          size="sm"
                          :disabled="swapping"
                          @click="requestSwap(assignment)"
                        >
                          {{ swapping ? 'Processing...' : `Request a swap with ${assignment.personFirstName}` }}
                        </MaterialButton>
                      </template>
                      <span v-else class="text-muted text-sm">
                        You are unavailable for this assignment
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

const route = useRoute()
const toast = useToast()

// State
const loading = ref(true)
const swapping = ref(false)
const submittedSwaps = ref(new Set())
const searchTerm = ref('')
const currentAssignment = ref(null)
const assignments = ref([])
const introText = ref('')

// Mock fallback data
const mockCurrentAssignment = {
  activity: 'Sunday Morning Service -main',
  group: 'Usher',
  date: 'Dec 28, 2025 10:00:00 AM',
  personAssigned: 'Konadu A'
}
const mockAssignments = [
  { activity: 'Sunday Evening Service', workgroup: '[id: 39635] Ushers', date: 'Dec 28, 2025 6:00:00 PM', personAssigned: 'Tel C.', personFirstName: 'Tel', available: true },
  { activity: 'Sunday Evening Service', workgroup: '[id: 39635] Ushers', date: 'Dec 28, 2025 6:00:00 PM', personAssigned: 'Tiffany Ransoner', personFirstName: 'Tiffany', available: true },
  { activity: 'Sunday Evening Service', workgroup: '[id: 39635] Ushers', date: 'Dec 28, 2025 6:00:00 PM', personAssigned: 'Soph taylor', personFirstName: 'Soph', available: true },
  { activity: 'Sunday Morning Service -main', workgroup: '[id: 39685] Usher', date: 'Jan 4, 2026 10:00:00 AM', personAssigned: 'Angie Baron', personFirstName: 'Angie', available: false },
  { activity: 'Sunday Morning Service -main', workgroup: '[id: 39685] Usher', date: 'Jan 4, 2025 10:00:00 AM', personAssigned: 'Sharjeel Ahmed', personFirstName: 'Sharjeel', available: false },
  { activity: 'Sunday Evening Service', workgroup: '[id: 39639] Ushers', date: 'Jan 4, 2026 6:00:00 PM', personAssigned: 'Philippa A', personFirstName: 'Philippa', available: true },
  { activity: 'Sunday Evening Service', workgroup: '[id: 39639] Ushers', date: 'Jan 4, 2025 6:00:00 PM', personAssigned: 'Ronald madson', personFirstName: 'Ronald', available: true },
  { activity: 'Sunday Evening Service', workgroup: '[id: 39639] Ushers', date: 'Jan 4, 2025 6:00:00 PM', personAssigned: 'Sharjeel Ahmed', personFirstName: 'Sharjeel', available: true },
  { activity: 'Sunday Morning Service -main', workgroup: '[id: 39696] Usher', date: 'Jan 11, 2025 10:00:00 AM', personAssigned: 'Tiffany Ransoner', personFirstName: 'Tiffany', available: true },
  { activity: 'Sunday Morning Service -main', workgroup: '[id: 39696] Usher', date: 'Jan 11, 2026 10:00:00 AM', personAssigned: 'Barbara Manchandurana', personFirstName: 'Barbara', available: true }
]
const mockIntroText = 'This page allows you to view a list of upcoming assignments that you can potentially swap with.'

// Computed display values (prefer API, fallback to mock)
const displayCurrentAssignment = computed(() => currentAssignment.value || mockCurrentAssignment)
const displayAssignments = computed(() =>
  (assignments.value && assignments.value.length > 0) ? assignments.value : mockAssignments
)
const displayIntroText = computed(() => introText.value || mockIntroText)
const filteredAssignments = computed(() => {
  if (!searchTerm.value) return displayAssignments.value
  const term = searchTerm.value.toLowerCase()
  return displayAssignments.value.filter(a =>
    a.activity.toLowerCase().includes(term) ||
    a.workgroup.toLowerCase().includes(term) ||
    a.personAssigned.toLowerCase().includes(term) ||
    a.date.toLowerCase().includes(term)
  )
})

// Fetch page details on mount
const fetchSwapDetails = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) {
    loading.value = false
    return
  }

  try {
    loading.value = true
    const response = await api.get(`/account/selectswap-details/cs/${encodedString}`)

    if (response.data) {
      const data = response.data

      if (data.currentAssignment) {
        currentAssignment.value = data.currentAssignment
      }
      if (data.assignments && Array.isArray(data.assignments)) {
        assignments.value = data.assignments
      }
      if (data.introText) introText.value = data.introText
    }
  } catch (error) {
    console.error('Error fetching select swap details:', error)
  } finally {
    loading.value = false
  }
}

// Build a unique key for an assignment row (used to track submitted swaps)
const getAssignmentKey = (assignment) => {
  return `${assignment.activity}_${assignment.workgroup}_${assignment.date}_${assignment.personAssigned}`
}

// Request swap action
const requestSwap = async (assignment) => {
  const encodedString = route.params.encodedString
  if (!encodedString || swapping.value) return

  swapping.value = true
  try {
    const response = await api.post(`/account/selectswap/cs/${encodedString}`, {
      assignmentId: assignment.id,
      activity: assignment.activity,
      workgroup: assignment.workgroup,
      date: assignment.date,
      personAssigned: assignment.personAssigned
    })
    const msg = response.data?.message || `Swap requested with ${assignment.personAssigned}`
    toast.success(msg, { position: 'top-right' })
    // Mark this assignment as submitted so the button is replaced
    submittedSwaps.value.add(getAssignmentKey(assignment))
  } catch (error) {
    console.error('Error requesting swap:', error)
    const errMsg = error.response?.data?.message || error.message || 'Failed to request swap.'
    toast.error(errMsg, { position: 'top-right' })
  } finally {
    swapping.value = false
  }
}

const viewPendingSwaps = () => {
  // Navigate to pending swaps page or show modal
  console.log('Viewing pending swap requests')
}

onMounted(async () => {
  await fetchSwapDetails()
})
</script>

<style scoped>
.table {
  width: 100%;
  margin-bottom: 1rem;
  color: #212529;
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-bordered th,
.table-bordered td {
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.table thead th {
  vertical-align: bottom;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
}
</style>

